import { useState } from 'react';
import api from '../../api/axios';

const BHK_TYPES = ["1 BHK", "2 BHK", "3 BHK", "4 BHK", "Villa", "Office Space", "Other"];
const POSSESSION_OPTS = ["Immediately", "In a Month", "2-3 Month", "3-6 Month", "0.6-1 Year"];
const STAGE_OPTS = [
  "Lead Received", "Call NR", "Not Interested", "Irrelevant Lead",
  "In Progress", "Quotation Sent", "Site Visit Done",
  "Office Visit Done", "Revised Quotation Sent", "Client Loss", "Client Won"
];

export default function DashboardLeadModal({ isOpen, onClose, onSuccess, salespersons = [] }) {
  const [form, setForm] = useState({
    clientName: '',
    phone: '',
    email: '',
    estBudget: '',
    bhkType: '2 BHK',
    possessionMonth: 'In a Month',
    stage: 'Lead Received',
    propertyAddress: '',
    city: 'Pune',
    state: 'Maharashtra',
    pincode: '',
    location: '',
    assignedTo: '',
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.clientName.trim()) {
      setError('Client full name is required.');
      return;
    }
    if (!form.phone.trim()) {
      setError('Phone number is required.');
      return;
    }
    const budget = parseFloat(form.estBudget);
    if (!budget || budget < 1) {
      setError('Estimated budget must be at least ₹1.');
      return;
    }
    if (!form.propertyAddress.trim()) {
      setError('Property address is required.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        clientName: form.clientName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        estBudget: budget,
        assignedTo: form.assignedTo || null,
      };

      const res = await api.post('/projects', payload);
      const createdProject = res.data?.data?.project || res.data?.project;
      onSuccess?.('Lead added successfully!', createdProject);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save lead. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal dash-action-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3 className="modal-title">+ Add New Lead</h3>
            <p style={{ margin: '3px 0 0 0', fontSize: '0.8rem', color: 'var(--muted)' }}>
              Capture a new prospect inquiry into the sales pipeline
            </p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ maxHeight: 'calc(80vh - 120px)', overflowY: 'auto' }}>
            {error && (
              <div className="alert alert-error" style={{ marginBottom: '1rem', padding: '0.65rem 0.9rem', fontSize: '0.85rem' }}>
                {error}
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Client Name *</label>
                <input
                  type="text"
                  name="clientName"
                  className="form-input"
                  placeholder="e.g. Rahul Sharma"
                  value={form.clientName}
                  onChange={handleChange}
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  className="form-input"
                  placeholder="e.g. 9876543210"
                  value={form.phone}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  placeholder="client@email.com"
                  value={form.email}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Est. Budget (₹) *</label>
                <input
                  type="number"
                  name="estBudget"
                  className="form-input"
                  placeholder="e.g. 750000"
                  value={form.estBudget}
                  onChange={handleChange}
                  min="1"
                  step="1"
                  required
                />
              </div>
            </div>

            <div className="form-row-3">
              <div className="form-group">
                <label className="form-label">BHK / Space *</label>
                <select
                  name="bhkType"
                  className="form-select"
                  value={form.bhkType}
                  onChange={handleChange}
                  required
                >
                  {BHK_TYPES.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Possession *</label>
                <select
                  name="possessionMonth"
                  className="form-select"
                  value={form.possessionMonth}
                  onChange={handleChange}
                  required
                >
                  {POSSESSION_OPTS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Stage *</label>
                <select
                  name="stage"
                  className="form-select"
                  value={form.stage}
                  onChange={handleChange}
                  required
                >
                  {STAGE_OPTS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Assign To (Sales / BD)</label>
              <select
                name="assignedTo"
                className="form-select"
                value={form.assignedTo}
                onChange={handleChange}
              >
                <option value="">Unassigned (Open Pool)</option>
                {salespersons.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name} {s.department ? `(${s.department})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Property Address *</label>
              <input
                type="text"
                name="propertyAddress"
                className="form-input"
                placeholder="e.g. Flat 402, Rohan Viti, Baner"
                value={form.propertyAddress}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row-3">
              <div className="form-group">
                <label className="form-label">City *</label>
                <input
                  type="text"
                  name="city"
                  className="form-input"
                  placeholder="City"
                  value={form.city}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">State *</label>
                <input
                  type="text"
                  name="state"
                  className="form-input"
                  placeholder="State"
                  value={form.state}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Pincode</label>
                <input
                  type="text"
                  name="pincode"
                  className="form-input"
                  placeholder="e.g. 411045"
                  value={form.pincode}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Landmark / Project Name</label>
              <input
                type="text"
                name="location"
                className="form-input"
                placeholder="e.g. Near Symbiosis College"
                value={form.location}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
              style={{ background: '#976793' }}
            >
              {saving ? 'Creating Lead...' : 'Create Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
